import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  Grid,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import client from '../../api/client';
import {
  ACTIVITY_TYPE_TEMPLATES,
  createDraftFromTemplate,
  createEmptyActivityTypeDraft,
  getActivityTypeTemplate,
} from './activityTypeTemplates';
import {
  ACTIVITY_TYPE_DIALOG_STEPS,
  buildActivityTypeDialogPreview,
  getDialogQuestion4bMode,
  getDialogServiceKind,
  getDialogTimeSelectionMode,
  updateDraftQuestion4bMode,
  updateDraftServiceKind,
  updateDraftTimeSelectionMode,
} from './activityTypeDialogModel';

function mapRowToDraft(row) {
  return {
    id: row.id,
    name: row.name || '',
    isActive: Boolean(row.isActive),
    isDayService: Boolean(row.isDayService),
    visibilityMode: String(row.visibilityMode || 'base'),
    supportsA3Za3: Boolean(row.supportsA3Za3),
    supportsTGroup: Boolean(row.supportsTGroup),
    supportsHonorar: Boolean(row.supportsHonorar),
    keywordMode: String(row.keywordMode || 'optional'),
    hoursQuestionMode: String(row.hoursQuestionMode || 'required'),
    responsibleQuestionMode: String(row.responsibleQuestionMode || 'uebungsleiter'),
    responsibleQuestionText: String(row.responsibleQuestionText || ''),
    eventContextMode: String(row.eventContextMode || 'veranstaltung'),
    timeQuestionText: String(row.timeQuestionText || ''),
    timeEntryMode: String(row.timeEntryMode || 'custom-only'),
    autoResponsibleMode: String(row.autoResponsibleMode || 'none'),
    pauseMode: String(row.pauseMode || 'hidden-no-deduction'),
    pdfProtocolMode: String(row.pdfProtocolMode || 'hidden'),
    serviceDurationLabel: String(row.serviceDurationLabel || 'Dienstzeit (abzgl. Pause)'),
    protocolCode: String(row.protocolCode || ''),
    scaleWorkHours: Boolean(row.scaleWorkHours),
    sortOrder: Number(row.sortOrder || 999),
  };
}

function buildPayload(draft) {
  return {
    name: String(draft.name || '').trim(),
    isActive: Boolean(draft.isActive),
    isDayService: Boolean(draft.isDayService),
    visibilityMode: String(draft.visibilityMode || 'base'),
    supportsA3Za3: Boolean(draft.supportsA3Za3),
    supportsTGroup: Boolean(draft.supportsTGroup),
    supportsHonorar: Boolean(draft.supportsHonorar),
    keywordMode: String(draft.keywordMode || 'optional'),
    hoursQuestionMode: String(draft.hoursQuestionMode || 'required'),
    responsibleQuestionMode: String(draft.responsibleQuestionMode || 'uebungsleiter'),
    responsibleQuestionText: String(draft.responsibleQuestionText || '').trim(),
    eventContextMode: String(draft.eventContextMode || 'veranstaltung'),
    timeQuestionText: String(draft.timeQuestionText || '').trim(),
    timeEntryMode: String(draft.timeEntryMode || 'custom-only'),
    autoResponsibleMode: String(draft.autoResponsibleMode || 'none'),
    pauseMode: String(draft.pauseMode || 'hidden-no-deduction'),
    pdfProtocolMode: String(draft.pdfProtocolMode || 'hidden'),
    serviceDurationLabel: String(draft.serviceDurationLabel || '').trim(),
    protocolCode: String(draft.protocolCode || '').trim(),
    scaleWorkHours: Boolean(draft.scaleWorkHours),
    sortOrder: Number(draft.sortOrder || 999),
  };
}

function getStepIndex(stepId) {
  const index = ACTIVITY_TYPE_DIALOG_STEPS.findIndex((step) => step.id === stepId);
  return index >= 0 ? index : 0;
}

function getPreviewCardBorder(isActive) {
  return isActive ? '2px solid rgba(27, 58, 92, 0.5)' : '1px solid rgba(27, 58, 92, 0.12)';
}

function TaetigkeitenPage() {
  const [rows, setRows] = useState([]);
  const [draft, setDraft] = useState(createEmptyActivityTypeDraft());
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [selectedTemplateId, setSelectedTemplateId] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [activeStepIndex, setActiveStepIndex] = useState(0);

  const isEditing = Number.isFinite(Number(draft.id));

  const loadRows = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const response = await client.get('/api/admin/activity-types');
      setRows(response.data?.activityTypes || []);
    } catch (loadError) {
      setError(loadError.response?.data?.error || 'Tätigkeiten konnten nicht geladen werden.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadRows();
  }, [loadRows]);

  const sortedRows = useMemo(
    () => [...rows].sort((left, right) => Number(left.sortOrder || 999) - Number(right.sortOrder || 999)),
    [rows],
  );

  const previewItems = useMemo(() => buildActivityTypeDialogPreview(draft), [draft]);
  const activeStep = ACTIVITY_TYPE_DIALOG_STEPS[activeStepIndex] || ACTIVITY_TYPE_DIALOG_STEPS[0];
  const serviceKind = getDialogServiceKind(draft);
  const timeSelectionMode = getDialogTimeSelectionMode(draft);
  const question4bMode = getDialogQuestion4bMode(draft);

  const resetDialog = () => {
    setDraft(createEmptyActivityTypeDraft());
    setSelectedTemplateId('');
    setActiveStepIndex(0);
  };

  const handleCloseDialog = () => {
    if (saving) {
      return;
    }

    setDialogOpen(false);
    resetDialog();
  };

  const openCreateDialog = () => {
    resetDialog();
    setDialogOpen(true);
    setError('');
    setMessage('');
  };

  const openEditDialog = (row) => {
    setDraft(mapRowToDraft(row));
    setSelectedTemplateId('');
    setActiveStepIndex(1);
    setDialogOpen(true);
    setError('');
    setMessage('');
  };

  const updateDraft = (changes) => {
    setDraft((prev) => ({
      ...prev,
      ...changes,
    }));
  };

  const handleApplyTemplate = (templateId) => {
    const template = getActivityTypeTemplate(templateId);
    setDraft(createDraftFromTemplate(templateId));
    setSelectedTemplateId(templateId);
    setActiveStepIndex(1);
    setError('');
    setMessage(
      template
        ? `Vorlage "${template.label}" geladen. Die Auswirkungen sind links sofort sichtbar.`
        : 'Vorlage geladen.',
    );
  };

  const handleServiceKindChange = (nextKind) => {
    setDraft((prev) => updateDraftServiceKind(prev, nextKind));
  };

  const handleTimeSelectionModeChange = (nextMode) => {
    setDraft((prev) => updateDraftTimeSelectionMode(prev, nextMode));
  };

  const handleQuestion4bModeChange = (nextMode) => {
    setDraft((prev) => updateDraftQuestion4bMode(prev, nextMode));
  };

  const handleFullDayWorkflowChange = (nextWorkflow) => {
    if (nextWorkflow === 'te1-flex-honorar') {
      updateDraft({
        isDayService: true,
        timeEntryMode: 'te1-flex-honorar',
        pauseMode: 'question',
        scaleWorkHours: true,
        supportsA3Za3: false,
        supportsTGroup: true,
        supportsHonorar: true,
      });
      return;
    }

    updateDraft({
      isDayService: true,
      timeEntryMode: 'a3-za3',
      pauseMode: 'question',
      scaleWorkHours: true,
      supportsA3Za3: true,
      supportsTGroup: false,
      supportsHonorar: false,
    });
  };

  const handleSave = async () => {
    const payload = buildPayload(draft);
    if (!payload.name) {
      setError('Bitte einen Namen für die Tätigkeit eintragen.');
      setActiveStepIndex(getStepIndex('question-2'));
      return;
    }

    try {
      setSaving(true);
      setError('');
      setMessage('');

      if (isEditing) {
        await client.put(`/api/admin/activity-types/${draft.id}`, payload);
        setMessage('Tätigkeit aktualisiert.');
      } else {
        await client.post('/api/admin/activity-types', payload);
        setMessage('Tätigkeit erstellt.');
      }

      setDialogOpen(false);
      resetDialog();
      await loadRows();
    } catch (saveError) {
      setError(saveError.response?.data?.error || 'Tätigkeit konnte nicht gespeichert werden.');
    } finally {
      setSaving(false);
    }
  };

  const handleDeactivate = async (id) => {
    try {
      setSaving(true);
      setError('');
      setMessage('');
      await client.delete(`/api/admin/activity-types/${id}`);
      setMessage('Tätigkeit deaktiviert.');
      await loadRows();
      if (Number(draft.id) === Number(id)) {
        resetDialog();
      }
    } catch (deleteError) {
      setError(deleteError.response?.data?.error || 'Tätigkeit konnte nicht deaktiviert werden.');
    } finally {
      setSaving(false);
    }
  };

  const renderStepEditor = () => {
    if (activeStep.id === 'question-1') {
      return (
        <Stack spacing={2}>
          <Typography variant="body1">
            Links siehst du die Datumsfrage des echten Wizards. Für diesen Schritt gibt es bewusst keine Einstellung, weil Frage 1 für alle Tätigkeiten gleich bleibt.
          </Typography>
          <Alert severity="info">Die eigentliche Konfiguration beginnt mit Frage 2.</Alert>
        </Stack>
      );
    }

    if (activeStep.id === 'question-2') {
      return (
        <Stack spacing={2}>
          <TextField
            select
            fullWidth
            size="small"
            label="Vorlage"
            value={selectedTemplateId}
            onChange={(event) => handleApplyTemplate(event.target.value)}
            helperText="Optional. Vorlagen belegen die Grundkonfiguration direkt vor."
          >
            <MenuItem value="">Keine Vorlage</MenuItem>
            {ACTIVITY_TYPE_TEMPLATES.map((template) => (
              <MenuItem key={template.id} value={template.id}>
                {template.label}
              </MenuItem>
            ))}
          </TextField>

          <Grid container spacing={1}>
            {ACTIVITY_TYPE_TEMPLATES.map((template) => (
              <Grid item xs={12} sm={6} key={template.id}>
                <Card variant="outlined" sx={{ height: '100%' }}>
                  <CardContent sx={{ p: 1.25 }}>
                    <Stack spacing={1}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                        {template.label}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {template.description}
                      </Typography>
                      <Button size="small" variant="outlined" onClick={() => handleApplyTemplate(template.id)}>
                        Vorlage laden
                      </Button>
                    </Stack>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>

          <TextField
            fullWidth
            label="Name der Tätigkeit"
            value={draft.name}
            onChange={(event) => updateDraft({ name: event.target.value })}
            size="small"
          />
          <TextField
            fullWidth
            label="Sortierung"
            value={draft.sortOrder}
            onChange={(event) => updateDraft({ sortOrder: event.target.value })}
            size="small"
            type="number"
          />
          <TextField
            select
            fullWidth
            size="small"
            label="Dienstart"
            value={serviceKind}
            onChange={(event) => handleServiceKindChange(event.target.value)}
          >
            <MenuItem value="24h-service">Veranstaltung im 24h-Dienst</MenuItem>
            <MenuItem value="day-service">Veranstaltung im Tagesdienst</MenuItem>
          </TextField>
          <TextField
            select
            fullWidth
            size="small"
            label="Aktiv"
            value={draft.isActive ? 'yes' : 'no'}
            onChange={(event) => updateDraft({ isActive: event.target.value === 'yes' })}
          >
            <MenuItem value="yes">Ja</MenuItem>
            <MenuItem value="no">Nein</MenuItem>
          </TextField>
        </Stack>
      );
    }

    if (activeStep.id === 'question-3') {
      return (
        <Stack spacing={2}>
          <TextField
            select
            fullWidth
            size="small"
            label="Standardtext für Verantwortliche"
            value={draft.responsibleQuestionMode}
            onChange={(event) => updateDraft({ responsibleQuestionMode: event.target.value })}
          >
            <MenuItem value="uebungsleiter">Übungsleiter / Verantwortlicher</MenuItem>
            <MenuItem value="einheitsfuehrer">Einheitsführer</MenuItem>
            <MenuItem value="geraetepruefer">Geräteprüfer</MenuItem>
            <MenuItem value="lehrgangsleiter">Lehrgangsleiter</MenuItem>
          </TextField>
          <TextField
            fullWidth
            size="small"
            label="Eigener Fragetext"
            value={draft.responsibleQuestionText}
            onChange={(event) => updateDraft({ responsibleQuestionText: event.target.value })}
            helperText="Leer lassen, um den Standardtext des Wizards zu verwenden."
          />
          <TextField
            select
            fullWidth
            size="small"
            label="Verantwortlicher"
            value={draft.autoResponsibleMode}
            onChange={(event) => updateDraft({ autoResponsibleMode: event.target.value })}
          >
            <MenuItem value="none">Auswählbar durch den Nutzer</MenuItem>
            <MenuItem value="current-user">Automatisch aktueller Nutzer</MenuItem>
          </TextField>
        </Stack>
      );
    }

    if (activeStep.id === 'question-4') {
      return (
        <Stack spacing={2}>
          <TextField
            select
            fullWidth
            size="small"
            label="Kontext für die Zeitraum-Frage"
            value={draft.eventContextMode}
            onChange={(event) => updateDraft({ eventContextMode: event.target.value })}
          >
            <MenuItem value="veranstaltung">Veranstaltung</MenuItem>
            <MenuItem value="uebung">Übung</MenuItem>
            <MenuItem value="einsatz">Einsatz</MenuItem>
            <MenuItem value="geraetepruefung">Geräteprüfung</MenuItem>
          </TextField>
          <TextField
            fullWidth
            size="small"
            label="Eigener Zeitraum-Fragetext"
            value={draft.timeQuestionText}
            onChange={(event) => updateDraft({ timeQuestionText: event.target.value })}
          />
          <TextField
            select
            fullWidth
            size="small"
            label="Zeitraum-Art"
            value={timeSelectionMode}
            onChange={(event) => handleTimeSelectionModeChange(event.target.value)}
            helperText="Die Admin-Sprache arbeitet mit individuell, vordefiniert und vollständiger Tagesdienst."
          >
            <MenuItem value="individual">Individueller Zeitraum</MenuItem>
            <MenuItem value="predefined">Vordefinierter Zeitraum</MenuItem>
            <MenuItem value="full-day-service">Vollständiger Tagesdienst</MenuItem>
          </TextField>

          {timeSelectionMode === 'individual' && (
            <Alert severity="info">
              Der Wizard zeigt direkt die Zeitfelder Von und Bis. Eine mögliche Folgefrage 4c erscheint nur, wenn der Zeitraum nahe an einem vollständigen Tagesdienst liegt.
            </Alert>
          )}

          {timeSelectionMode === 'predefined' && (
            <Alert severity="info">
              Im aktuellen Datenmodell bedeutet vordefiniert: Der Wizard bietet einen Standardzeitraum an und zusätzlich weiterhin eine individuelle Alternative.
            </Alert>
          )}

          {timeSelectionMode === 'full-day-service' ? (
            <>
              <TextField
                select
                fullWidth
                size="small"
                label="Tagesdienst-Ablauf"
                value={draft.timeEntryMode === 'te1-flex-honorar' ? 'te1-flex-honorar' : 'a3-za3'}
                onChange={(event) => handleFullDayWorkflowChange(event.target.value)}
              >
                <MenuItem value="a3-za3">Vollständiger Tagesdienst A3/zA3</MenuItem>
                <MenuItem value="te1-flex-honorar">Vollständiger Tagesdienst T/Honorar</MenuItem>
              </TextField>

              {draft.timeEntryMode === 'a3-za3' ? (
                <Alert severity="info">
                  Bei A3/zA3 bleibt Frage 4b ausgeblendet. Das Kürzel A3 oder zA3 wird später anhand der Dienstzeit automatisch zugeordnet.
                </Alert>
              ) : (
                <>
                  <TextField
                    select
                    fullWidth
                    size="small"
                    label="Frage 4b: Dienstkürzel"
                    value={question4bMode}
                    onChange={(event) => handleQuestion4bModeChange(event.target.value)}
                    helperText="Hier legst du fest, ob T oder Honorar später auswählbar sind oder bereits vordefiniert versteckt bleiben."
                  >
                    <MenuItem value="visible">Einblenden: T oder Honorar auswählbar</MenuItem>
                    <MenuItem value="fixed-t">Ausblenden: fest auf T (TE1/2 automatisch)</MenuItem>
                    <MenuItem value="fixed-honorar">Ausblenden: fest auf Honorar</MenuItem>
                  </TextField>
                  <Alert severity="info">
                    Bei vollständigem Tagesdienst wird die Tagesdienst-Pausenlogik automatisch aktiv. Die linke Vorschau zeigt direkt, ob daraus eine eigene Folgefrage für das Dienstkürzel entsteht.
                  </Alert>
                </>
              )}
            </>
          ) : (
            <>
              <TextField
                select
                fullWidth
                size="small"
                label="Tagesdienst-Pausenlogik anwenden"
                value={draft.pauseMode === 'question' ? 'yes' : 'no'}
                onChange={(event) => updateDraft({ pauseMode: event.target.value === 'yes' ? 'question' : 'hidden-no-deduction' })}
                helperText="Ja bedeutet: Schwellenlogik mit >6h / >9h. Nein bedeutet: keine Pausenfrage und keine Abzüge."
              >
                <MenuItem value="yes">Ja</MenuItem>
                <MenuItem value="no">Nein</MenuItem>
              </TextField>
              <Alert severity={draft.pauseMode === 'question' ? 'info' : 'warning'}>
                {draft.pauseMode === 'question'
                  ? 'Die Dienstzeit wird bei Pause = Nein nach den bekannten Schwellen korrigiert: bis 6h kein Abzug, über 6h bis 9h 30 Minuten, über 9h 45 Minuten.'
                  : 'Die Pausenlogik bleibt komplett ausgeschaltet. Die linke Vorschau zeigt deshalb keine Pausenfrage und keine Abzüge.'}
              </Alert>
            </>
          )}

          <TextField
            fullWidth
            size="small"
            label="Label für Dienstzeit"
            value={draft.serviceDurationLabel}
            onChange={(event) => updateDraft({ serviceDurationLabel: event.target.value })}
          />
        </Stack>
      );
    }

    if (activeStep.id === 'question-5') {
      return (
        <Stack spacing={2}>
          <TextField
            select
            fullWidth
            size="small"
            label="Frage 5 einblenden"
            value={draft.hoursQuestionMode === 'hidden' ? 'no' : 'yes'}
            onChange={(event) => updateDraft({ hoursQuestionMode: event.target.value === 'yes' ? 'required' : 'hidden' })}
            helperText="Nein bedeutet: die Stunden werden automatisch mit 0 gesetzt."
          >
            <MenuItem value="yes">Ja</MenuItem>
            <MenuItem value="no">Nein</MenuItem>
          </TextField>
          <TextField
            select
            fullWidth
            size="small"
            label="Schlagwort-Feld"
            value={draft.keywordMode}
            onChange={(event) => updateDraft({ keywordMode: event.target.value })}
          >
            <MenuItem value="optional">Optional</MenuItem>
            <MenuItem value="required">Erforderlich</MenuItem>
            <MenuItem value="hidden">Ausblenden</MenuItem>
          </TextField>
        </Stack>
      );
    }

    return (
      <Stack spacing={2}>
        <TextField
          select
          fullWidth
          size="small"
          label="Sichtbarkeit"
          value={draft.visibilityMode}
          onChange={(event) => updateDraft({ visibilityMode: event.target.value })}
        >
          <MenuItem value="base">Standard sichtbar</MenuItem>
          <MenuItem value="instructor">Nur Ausbilder/Einheitsführer</MenuItem>
          <MenuItem value="equipment-day">Nur Geräteprüfung-Tagesdienst</MenuItem>
          <MenuItem value="srht-training">Nur SRHT-Grundlehrgang/Bewerber</MenuItem>
        </TextField>
        <TextField
          select
          fullWidth
          size="small"
          label="PDF-Dienstzeitprotokoll"
          value={draft.pdfProtocolMode}
          onChange={(event) => updateDraft({ pdfProtocolMode: event.target.value })}
        >
          <MenuItem value="hidden">Nicht anbieten</MenuItem>
          <MenuItem value="ask">Abfrage anzeigen</MenuItem>
        </TextField>
        <TextField
          fullWidth
          size="small"
          label="Protokollcode"
          value={draft.protocolCode}
          onChange={(event) => updateDraft({ protocolCode: event.target.value })}
        />
        <FormControlLabel
          control={(
            <Checkbox
              checked={Boolean(draft.scaleWorkHours)}
              onChange={(event) => updateDraft({ scaleWorkHours: event.target.checked })}
            />
          )}
          label="Dienstzeit für Statistik/PDF skalieren"
        />
      </Stack>
    );
  };

  return (
    <Box sx={{ px: { xs: 2, md: 4 }, py: 2 }}>
      <Stack spacing={2}>
        <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" spacing={1} alignItems={{ sm: 'center' }}>
          <Typography variant="h4" sx={{ fontWeight: 700 }}>
            Arten von Tätigkeiten
          </Typography>
          <Button variant="contained" onClick={openCreateDialog} disabled={saving}>
            Neue Tätigkeit erstellen
          </Button>
        </Stack>

        {error && <Alert severity="error">{error}</Alert>}
        {message && <Alert severity="success">{message}</Alert>}

        <Card sx={{ borderRadius: 3 }}>
          <CardContent>
            <Stack spacing={1.25}>
              <Typography variant="h6" sx={{ fontWeight: 700 }}>
                Bestehende Tätigkeiten
              </Typography>

              {loading ? (
                <Stack direction="row" spacing={1} alignItems="center">
                  <CircularProgress size={20} />
                  <Typography variant="body2">Lädt...</Typography>
                </Stack>
              ) : (
                <Stack spacing={1}>
                  {sortedRows.map((row) => (
                    <Card key={row.id} variant="outlined" sx={{ borderRadius: 2 }}>
                      <CardContent sx={{ py: 1.25 }}>
                        <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" spacing={1}>
                          <Stack spacing={0.5}>
                            <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                              {row.name}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {`Sortierung: ${row.sortOrder} | Aktiv: ${row.isActive ? 'Ja' : 'Nein'} | Sichtbarkeit: ${row.visibilityMode || 'base'} | Zeitraum: ${row.timeEntryMode || 'custom-only'} | Pause: ${row.pauseMode || 'hidden-no-deduction'} | PDF: ${row.pdfProtocolMode || 'hidden'} | Frage 5: ${row.hoursQuestionMode || 'required'}`}
                            </Typography>
                          </Stack>
                          <Stack direction="row" spacing={1}>
                            <Button size="small" variant="outlined" onClick={() => openEditDialog(row)} disabled={saving}>
                              Bearbeiten
                            </Button>
                            <Button size="small" color="error" variant="text" onClick={() => handleDeactivate(row.id)} disabled={saving || !row.isActive}>
                              Deaktivieren
                            </Button>
                          </Stack>
                        </Stack>
                      </CardContent>
                    </Card>
                  ))}
                  {!sortedRows.length && (
                    <Typography variant="body2" color="text.secondary">
                      Keine Tätigkeiten vorhanden.
                    </Typography>
                  )}
                </Stack>
              )}
            </Stack>
          </CardContent>
        </Card>
      </Stack>

      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="xl" fullWidth>
        <DialogTitle>{isEditing ? 'Tätigkeit bearbeiten' : 'Neue Tätigkeit erstellen'}</DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <Stack spacing={1.25}>
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                  <Typography variant="h6" sx={{ fontWeight: 700 }}>
                    Linke Vorschau: entstehendes Abfrageschema
                  </Typography>
                  <Chip label={draft.name || 'Neue Tätigkeit'} color="primary" variant="outlined" />
                </Stack>

                {previewItems.map((item) => {
                  const isActivePreview = item.stepId === activeStep.id;
                  return (
                    <Card key={item.id} variant="outlined" sx={{ borderRadius: 2, border: getPreviewCardBorder(isActivePreview) }}>
                      <CardContent sx={{ py: 1.5 }}>
                        <Stack spacing={1}>
                          <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" spacing={1}>
                            <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                              {`${item.number}. ${item.title}`}
                            </Typography>
                            <Chip size="small" label={item.status} color={isActivePreview ? 'primary' : 'default'} />
                          </Stack>
                          <Typography variant="body2">{item.question}</Typography>
                          <Typography variant="body2">{item.answerPreview}</Typography>
                          {item.details?.map((detail) => (
                            <Typography key={detail} variant="caption" color="text.secondary">
                              {detail}
                            </Typography>
                          ))}
                        </Stack>
                      </CardContent>
                    </Card>
                  );
                })}
              </Stack>
            </Grid>

            <Grid item xs={12} md={6}>
              <Stack spacing={1.5}>
                <Typography variant="h6" sx={{ fontWeight: 700 }}>
                  Rechte Seite: Einstellungen für {activeStep.title}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {activeStep.description}
                </Typography>

                <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                  {ACTIVITY_TYPE_DIALOG_STEPS.map((step, index) => (
                    <Button
                      key={step.id}
                      size="small"
                      variant={index === activeStepIndex ? 'contained' : 'outlined'}
                      onClick={() => setActiveStepIndex(index)}
                    >
                      {step.title}
                    </Button>
                  ))}
                </Stack>

                <Divider />

                {renderStepEditor()}
              </Stack>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={handleCloseDialog} disabled={saving}>Abbrechen</Button>
          <Button onClick={() => setActiveStepIndex((prev) => Math.max(0, prev - 1))} disabled={saving || activeStepIndex === 0}>
            Zurück
          </Button>
          <Button
            onClick={() => setActiveStepIndex((prev) => Math.min(ACTIVITY_TYPE_DIALOG_STEPS.length - 1, prev + 1))}
            disabled={saving || activeStepIndex >= ACTIVITY_TYPE_DIALOG_STEPS.length - 1}
          >
            Weiter
          </Button>
          <Button variant="contained" onClick={handleSave} disabled={saving}>
            {isEditing ? 'Aktualisieren' : 'Erstellen'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default TaetigkeitenPage;
